// PortfolioUpdateFlow.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { InfoCircle } from 'react-bootstrap-icons';
import axiosInstance from '../../api/api';
import { API_ROUTES } from '../../config/api-routes';
import { Typeahead } from 'react-bootstrap-typeahead';
import axios from 'axios';
import { toast } from 'react-toastify';
import { normalizeUrl } from '../../utils/url';
// import { BsInfoCircle } from 'react-icons/bs';
// import axios from 'axios';

interface PortfolioUpdateFlowProps {
  show: boolean;
  onClose: () => void;
  userData: any;
  refreshUser?: () => void;
}

const PortfolioUpdateFlow: React.FC<PortfolioUpdateFlowProps> = ({
  show,
  onClose,
  userData,
  refreshUser,
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<any>(userData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countries, setCountries] = useState<string[]>([]);
  const [, setLoader] = useState<boolean>(false);
  const [availableStates, setAvailableStates] = useState<string[]>([]);

  useEffect(() => {
    if (!formData?.country) {
      setAvailableStates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const list = await fetchStatesByCountry(formData.country);
      if (!cancelled) setAvailableStates(list ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [formData?.country]);

  useEffect(() => {
    if (!show || !userData) return;
    setFormData(userData);
  }, [show, userData]);

  useEffect(() => {
    if (!formData?.country) {
      setAvailableStates([]);
      return;
    }

    let cancelled = false;
    (async () => {
      const list = await fetchStatesByCountry(formData.country);
      if (!cancelled) setAvailableStates(list);
    })();

    return () => {
      cancelled = true;
    };
  }, [formData?.country]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  //   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //     if (e.target.files?.[0]) {
  //       setFormData({ ...formData, photo: e.target.files[0] });
  //     }
  //   };

  /*   useEffect(() => {
    const loadUser = async () => {
      const userData = await fetchUserData();
      setUser(userData); // ✅ triggers re-render
    };
    loadUser();
  }, [user]); */

  const handleSubmitStep = async (exitEarly = false) => {
    setLoading(true);
    setError(null);
    try {
      // Instead of new FormData()
      const payload: Record<string, any> = {};

      // Add your allowed keys
      const allowedKeys = [
        'firstName',
        'lastName',
        'gender',
        'dateOfBirth',
        'country',
        'state',
        'city',
        'phoneNumber',
        'company',
        'willingToRelocate',
        'flexibleType',
        'workArrangements',
        'timeToStart',
        'linkedin',
        'portfolio',
        'otherSocial',
        'preferNegotiate',
      ];

      allowedKeys.forEach((key) => {
        if (!formData[key]) return;
        if (key === 'linkedin' || key === 'otherSocial' || key === 'portfolio') {
          payload[key] = normalizeUrl(formData[key]);
          return;
        }
        payload[key] = formData[key];
      });

      // Salary logic
      if (formData.preferNegotiate === 'Yes') {
        payload.salary = 'I prefer to negotiate';
      } else if (formData.salary) {
        payload.salary = formData.salary;
      }

      await axiosInstance.post(API_ROUTES.AUTH.COMPLETE_PROFILE, payload);

      if (exitEarly || step === 3) {
        refreshUser?.();
        toast.success('Profile saved successfully!');
        onClose();
        window.location.reload();
      } else {
        setStep((prev) => prev + 1);
        toast.success(`Step ${step} Profile saved`);
      }
    } catch {
      toast.error('Failed to save profile. Please try again.');
      setError('Failed to save. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepHeadings = [
    {
      title: "Let's Get to Know You",
      subtext: 'Fill in your basic details to personalize your experience.',
    },
    {
      title: "Let's Talk About Work",
      subtext: 'Help us understand your professional preferences.',
    },
    {
      title: 'Boost Your Profile',
      subtext: 'Add links and a photo so your profile stands out.',
    },
  ];

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Row>
              <Col>
                <Form.Label className="mb-2 pt-4">First Name</Form.Label>
                <Form.Control
                  name="firstName"
                  placeholder="John"
                  value={formData?.firstName || ''}
                  onChange={handleChange}
                />
              </Col>
              <Col>
                <Form.Label className="mb-2 pt-4">Last Name</Form.Label>
                <Form.Control
                  name="lastName"
                  placeholder="Doe"
                  value={formData?.lastName || ''}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Label className="mb-2 pt-4">Gender</Form.Label>
                <Form.Select name="gender" value={formData?.gender || ''} onChange={handleChange}>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Form.Select>
              </Col>
              <Col>
                <Form.Label className="mb-2 pt-4">Date Of Birth</Form.Label>
                <Form.Control
                  type="date"
                  name="dateOfBirth"
                  value={formData?.dateOfBirth || ''}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Label className="mb-2 pt-4">Country</Form.Label>
                <Typeahead
                  id="country-update"
                  options={countries}
                  selected={formData?.country ? [formData.country] : []}
                  onChange={(selected) => {
                    const country = selected.length > 0 ? selected[0] : '';
                    setFormData({
                      ...formData,
                      country: country as string,
                    });
                  }}
                  placeholder="Choose a country..."
                  clearButton
                  className="typeahead"
                />
              </Col>

              <Col>
                <Form.Label className="mb-2 pt-4">State</Form.Label>
                <Form.Select
                  name="state"
                  value={formData?.state || ''}
                  onChange={handleChange}
                  disabled={availableStates.length === 0}
                >
                  <option value="">Select</option>
                  {availableStates.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Label className="mb-2 pt-4">City</Form.Label>
                <Form.Control
                  name="city"
                  placeholder="New York City"
                  value={formData?.city || ''}
                  onChange={handleChange}
                />
              </Col>
              <Col>
                <Form.Label className="mb-2 pt-4">Phone Number</Form.Label>
                <Form.Control
                  name="phoneNumber"
                  placeholder="+1"
                  value={formData?.phoneNumber || ''}
                  onChange={handleChange}
                />
              </Col>
            </Row>
          </>
        );
      case 2:
        return (
          <>
            <Form.Label className="mb-2 pt-4">Current Company</Form.Label>
            <Form.Control
              className="mb-2"
              name="company"
              placeholder="Your Current Company"
              value={formData?.company || ''}
              onChange={handleChange}
            />
            <Row>
              <Col>
                <Form.Label className="mb-2 pt-4">Willing to Relocate</Form.Label>
                <Form.Select
                  name="willingToRelocate"
                  value={formData?.willingToRelocate || ''}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Form.Select>
              </Col>
              <Col>
                <Form.Label className="d-flex align-items-center gap-2 mb-2 pt-4">
                  Flexibility
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="flexibility-tooltip">
                        Choose how flexible you are with your working hours or location.
                      </Tooltip>
                    }
                  >
                    <div>
                      <InfoCircle size={12} />
                    </div>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Select
                  name="flexibleType"
                  value={formData?.flexibleType || ''}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </Form.Select>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Label className="mb-2 pt-4">Preferred Work Arrangements</Form.Label>
                <Form.Select
                  name="workArrangements"
                  value={formData?.workArrangements || ''}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Remote">Remote</option>
                  <option value="Onsite">Onsite</option>
                  <option value="Hybrid">Hybrid</option>
                </Form.Select>
              </Col>
              <Col>
                <Form.Label className="d-flex align-items-center gap-2 mb-2 pt-4">
                  Time to Start (in days)
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="flexibility-tooltip">
                        Choose how soon you can start a new job.
                      </Tooltip>
                    }
                  >
                    <div>
                      <InfoCircle size={12} />
                    </div>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  name="timeToStart"
                  placeholder="1 - X0"
                  value={formData?.timeToStart || ''}
                  onChange={handleChange}
                />
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Form.Label className="mb-2 pt-4">Salary Preferences</Form.Label>
                <Form.Select
                  name="preferNegotiate"
                  value={formData?.preferNegotiate || ''}
                  onChange={handleChange}
                >
                  <option value="">Select</option>
                  <option value="Yes">I prefer to negotiate</option>
                  <option value="No">Enter amount</option>
                </Form.Select>
              </Col>
            </Row>
            {/* Conditionally render salary input if not negotiating */}
            {formData?.preferNegotiate === 'No' && (
              <>
                <Form.Label className="d-flex align-items-center gap-2 mb-2 pt-4">
                  Expected Salary Range ($)
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="salary-tooltip">Choose your expected salary range.</Tooltip>
                    }
                  >
                    <div>
                      <InfoCircle size={12} />
                    </div>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Control
                  className="mt-2"
                  name="salary"
                  placeholder="1000 - X000"
                  value={isNaN(formData?.salary) ? '' : formData?.salary || ''}
                  onChange={handleChange}
                />
              </>
            )}
          </>
        );
      case 3:
        return (
          <>
            <Form.Label className="mb-2 pt-4">LinkedLn URL</Form.Label>
            <Form.Control
              className="mb-2"
              name="linkedin"
              placeholder="https://www.linkedin.com/in/..."
              value={formData?.linkedin || ''}
              onChange={handleChange}
            />
            <Form.Label className="mb-2 pt-4">Other Social / Portfolio URL</Form.Label>
            <Form.Control
              className="mb-2"
              name="otherSocial"
              placeholder="https://..."
              value={formData?.otherSocial || ''}
              onChange={handleChange}
            />
            {/* <Form.Control
              type="file"
              onChange={handleFileChange}
              accept="image/*"
            />
            <Form.Text muted>Maximum Upload Size: 2MB</Form.Text> */}
          </>
        );
    }
  };

  const getCountries = useCallback(async () => {
    try {
      const response = await axiosInstance.get('auth/get-countries');
      if (!response || response.status !== 200) {
        setLoader(false);
        return;
      }
      const data = response.data;

      setCountries(data);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    setLoader(true);
    getCountries().finally(() => setLoader(false));
  }, [getCountries]);

  /*   useEffect(() => {
    setLoader(true);
    getCountries();
  }, [getCountries, loader]); */

  const fetchStatesByCountry = async (country: string) => {
    try {
      const response = await axios.post('https://countriesnow.space/api/v0.1/countries/states', {
        country,
      });

      if (!response.data.error) {
        return response.data.data.states.map((s: { name: string }) => s.name);
      }
    } catch (err) {
      console.error('Failed to fetch states:', err);
    }
    return [];
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      centered
      scrollable
      size="lg"
      className="ss-app-modal portfolio-update-flow-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <div className="text-muted small mb-1">Step {step} of 3</div>
          <div className="text-success fw-bold">{stepHeadings[step - 1].title}</div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3">{stepHeadings[step - 1].subtext}</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form>{renderStep()}</Form>
      </Modal.Body>
      <Modal.Footer className="portfolio-update-flow-footer">
        {step > 1 ? (
          <Button variant="outline-secondary" onClick={() => setStep(step - 1)}>
            Previous
          </Button>
        ) : (
          <span />
        )}
        <div className="d-flex gap-2 ms-auto">
          <Button
            variant="outline-success"
            onClick={() => handleSubmitStep(true)}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Save & exit'}
          </Button>
          <Button variant="success" onClick={() => handleSubmitStep()} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : step === 3 ? 'Submit' : 'Next'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PortfolioUpdateFlow;
